import type { ActivityQuery } from '../options/activity-queries';

export const GRAPHQL_GATEWAY_PATH = '/gateway/api/graphql';

export interface GraphQLRequest {
	operationName?: string;
	query: string;
	variables?: {};
}

export interface Container {
	name: string;
}

export interface Contributor {
	accountId: string;
	name: string;
	picture: string;
}

export type ObjectIcon = URLIcon | EmojiIcon;

interface CommonIcon {
	fallbackIcon?: string; // Used for 3P graph results in case the 3P product icon asset isn't yet available in flight deck
	value: string;
}

interface URLIcon extends CommonIcon {
	type: 'URL';
}

interface EmojiIcon extends CommonIcon {
	fallback?: string;
	type: 'EMOJI';
}

export type RootContainerIdentifier = {
	ari: string;
	id: string;
	type: 'SITE' | 'OTHER';
};

export interface ObjectItem {
	creator?: string | null; // creator/owner of the resource - used primarily for 3P graph when we want something else in the container field
	deduplicateId: string;
	icon: ObjectIcon | null;
	id: string;
	localResourceId: string | null; // this is needed by Search to de-duplicate results
	name: string | null; // sometimes a draft will come in with a `null` name
	objectTypeName?: string | null; // e.g. 'Bug', 'Story', 'Task'
	product: string; // right now this could be 'jira'
	providerId?: string | null; // unique ID associated with a 3P product provider
	rootContainerIdentifier: RootContainerIdentifier;
	subProduct: string | null; // eg 'software'
	subtype?: string;
	type: ActivityObjectType;
	url: string;
}

export interface ActivityItem {
	actor?: Contributor;
	aggregatedActivityItems?: ActivityItem[];
	containers: Container[];
	contributors: Contributor[];
	eventType: ActivityEventType;
	object: ObjectItem;
	query: ActivityQuery;
	timestamp: string;
}

export interface GraphQLRequest {
	operationName?: string;
	query: string;
	variables?: {};
}

export interface ActivitiesEvent {
	eventType: ActivityEventType;
}

export interface ActivitiesJiraIssue {
	issueKey: string;
}

export type ActivitiesExtension = ActivitiesJiraIssue;

export interface ActivitiesObject {
	cloudId: string;
	containers?: Container[];
	contributors?: ContributorConnection[];
	events: ActivitiesEvent[];
	extension?: ActivitiesExtension;
	iconUrl: string;
	id: string;
	localResourceId: string;
	name: string;
	product: string; // right now this could be 'jira'
	type: ActivityObjectType;
	url: string;
}

export interface ContributorConnection {
	profile: {
		accountId: string;
		name: string;
		picture: string;
	} | null;
}

export interface ActivitiesConnectionItem {
	actor?: {
		accountId: string;
		name: string;
		picture: string;
	};
	id: string;
	object: ActivitiesObject;
	timestamp: string;
}

export interface ActivitiesConnection {
	nodes?: ActivitiesConnectionItem[];
}
export interface ActivitiesTabsMyActivitiesConnection {
	myActivities: {
		all?: ActivitiesConnection | null;
		viewed?: ActivitiesConnection | null;
		workedOn?: ActivitiesConnection | null;
	};
}
export interface ActivitiesTabsActivitiesConnection {
	all?: ActivitiesConnection | null;
	workedOn?: ActivitiesConnection | null;
}

export interface ActivitiesTabsConnection {
	activities: ActivitiesTabsMyActivitiesConnection | ActivitiesTabsActivitiesConnection | null;
	errors?: {
		message: string;
	}[];
}

export interface ActivitiesDataTabsConnection {
	data?: ActivitiesTabsConnection;
}

export enum VIEWED_EVENT {
	VIEWED = 'viewed',
	SMARTLINK_VIEWED = 'sl-viewed',
	SMARTLINK_CLICKED = 'sl-clicked',
}

export enum WORKED_EVENT {
	COMMENTED = 'commented',
	CREATED = 'created',
	EDITED = 'edited',
	PUBLISHED = 'published',
	UPDATED = 'updated',
}

export type ActivityEventType = VIEWED_EVENT | WORKED_EVENT;

export enum ActivityObjectType {
	BLOGPOST = 'BLOGPOST',
	COMMENT = 'COMMENT',
	ISSUE = 'ISSUE',
	PAGE = 'PAGE',
	PROJECT = 'PROJECT',
	SITE = 'SITE',
	SPACE = 'SPACE',
	TASK = 'TASK',
	// 3P Graph types below
	DOCUMENT = 'document',
	REMOTE_LINK = 'remote-link',
}

type ActivitiesDataQuery = 'all' | 'viewed' | 'workedOn';

export type ActivitiesData = Record<ActivitiesDataQuery, ActivityItem[]>;
